import React, { useState } from "react";
import {
  Calendar,
  User,
  FileText,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Download,
  Edit,
  ArrowLeft,
  Camera,
  MessageSquare,
  ChevronDown,
  FileSpreadsheet,
} from "lucide-react";
import { ChecklistResponse } from "./InspectionChecklist";
import DynamicAssetFieldsViewer from "./DynamicAssetFieldsViewer";
import { AssetData } from "../../screens/SafetyEquipment/AssetInspection";
import { S3TempUrl } from "../../utils/s3Client";

interface InspectionViewerProps {
  selectedAsset: AssetData;
  inspection: {
    id: string;
    assetId: string;
    assetName?: string;
    assetLocation?: string;
    attachments?: string[];
    checklistName?: string;
    inspectorName: string;
    inspectorEmail: string;
    inspectionDate: string;
    inspectionType: "routine" | "maintenance" | "emergency" | "compliance";
    overallStatus: "pass" | "fail" | "attention";
    overallComments: string;
    correctiveActions?: string;
    nextInspectionDate?: string;
    // Asset-specific fields
    assetHptOn?: string;
    assetHptDue?: string;
    assetRefilledOn?: string;
    assetRefilledDue?: string;
    assetAmcStatus?:
    | "active"
    | "inactive"
    | "expired"
    | "pending"
    | "not-applicable";
    assetAmcStartDate?: string;
    assetAmcEndDate?: string;
    assetAmcVendor?: string;
    responses: ChecklistResponse[];
    createdAt: string;
    updatedAt?: string;
    isDraft: boolean;
  };
  onClose: () => void;
  onEdit?: () => void;
  onDownloadReport?: (format: "pdf" | "excel") => void;
  canEdit?: boolean;
}

const InspectionViewer: React.FC<InspectionViewerProps> = ({
  inspection,
  selectedAsset,
  onClose,
  onEdit,
  onDownloadReport,
  canEdit = false,
}) => {
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const { getsignedurl } = S3TempUrl();

  const getFileNameFromUrl = (url: string) => {
    try {
      const withoutQuery = url.split("?")[0];
      const parts = withoutQuery.split("/");
      return decodeURIComponent(parts[parts.length - 1] || "attachment");
    } catch {
      return "attachment";
    }
  };

  const handleViewFile = async (url: string) => {
    try {
      const key = url.split("amazonaws.com/")[1] || getFileNameFromUrl(url);
      const signed = await getsignedurl(key, "application/octet-stream");
      window.open(signed, "_blank");
    } catch {
      window.open(url, "_blank");
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "pass":
        return <CheckCircle size={20} className="text-green-500" />;
      case "fail":
        return <XCircle size={20} className="text-red-500" />;
      default:
        return <AlertTriangle size={20} className="text-orange-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pass":
        return "bg-green-50 border-green-200 text-green-800";
      case "fail":
        return "bg-red-50 border-red-200 text-red-800";
      default:
        return "bg-orange-50 border-orange-200 text-orange-800";
    }
  };

  const getTypeColor = (type: string) => {
    const colors = {
      routine: "bg-blue-100 text-blue-800",
      maintenance: "bg-purple-100 text-purple-800",
      emergency: "bg-red-100 text-red-800",
      compliance: "bg-green-100 text-green-800",
    };
    return colors[type as keyof typeof colors] || "bg-gray-100 text-gray-800";
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Group responses by category for better organization
  const groupedResponses = inspection.responses.reduce((acc, response) => {
    // Use a default category since ChecklistResponse doesn't have category property
    const category = "Inspection Items";
    if (!acc[category]) acc[category] = [];
    acc[category].push(response);
    return acc;
  }, {} as Record<string, ChecklistResponse[]>);

  const getQuestionText = (itemId: string): string => {
    // Map common item IDs to readable questions
    const questionMap: Record<string, string> = {
      weight_check: "CO2 Cylinder Weight Check",
      pressure_gauge: "Pressure Gauge Reading",
      cylinder_condition: "Cylinder External Condition",
      safety_pin: "Safety Pin Integrity",
      discharge_test: "Discharge Mechanism Test",
      location_accessibility: "Location Accessibility",
      signage_visibility: "Signage Visibility",
      maintenance_tag: "Maintenance Tag Status",
      foam_level: "Foam Solution Level",
      nozzle_condition: "Nozzle and Hose Condition",
      foam_quality: "Foam Solution Quality",
      led_indicator: "LED Indicator Function",
      test_button: "Test Button Response",
      alarm_sound: "Alarm Sound Level",
      detector_cleanliness: "Detector Cleanliness",
      mounting_security: "Mounting Security",
      wiring_condition: "Wiring Condition",
      water_pressure: "Water Pressure Reading",
      sprinkler_head_condition: "Sprinkler Head Condition",
      obstruction_check: "Obstruction Check",
      pipe_condition: "Pipe Condition",
      valve_operation: "Valve Operation",
      alarm_test: "Alarm Test",
      container_integrity: "Container Integrity",
      labeling_complete: "Safety Labeling",
      sds_available: "Safety Data Sheet Available",
      storage_temperature: "Storage Temperature",
      ventilation_adequate: "Ventilation Adequacy",
      spill_kit_available: "Spill Kit Available",
      ppe_available: "PPE Available",
      expiry_date_check: "Expiry Date Check",
      physical_condition: "Physical Condition",
      functionality_test: "Functionality Test",
      safety_compliance: "Safety Compliance",
      location_appropriate: "Location Appropriate",
      overall_comments: "Additional Comments",
    };

    return (
      questionMap[itemId] ||
      itemId.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
    );
  };

  const renderValue = (response: ChecklistResponse) => {
    if (typeof response.value === "boolean") {
      return response.value ? "Yes" : "No";
    }
    if (typeof response.value === "number") {
      return `${response.value}`;
    }
    return String(response.value || "N/A");
  };
  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-4xl mx-auto px-4 space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={onClose}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft size={20} />
              <span>Back</span>
            </button>

            <div className="flex items-center gap-3">
              {inspection.isDraft && (
                <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm font-medium">
                  Draft
                </span>
              )}

              <div
                className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1 ${getStatusColor(
                  inspection.overallStatus
                )}`}
              >
                {getStatusIcon(inspection.overallStatus)}
                <span className="capitalize">{inspection.overallStatus}</span>
              </div>

              <div
                className={`px-3 py-1 rounded-full text-sm font-medium ${getTypeColor(
                  inspection.inspectionType
                )}`}
              >
                {inspection.inspectionType.charAt(0).toUpperCase() +
                  inspection.inspectionType.slice(1)}
              </div>
            </div>
          </div>

          <div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              Inspection Report
            </h1>
            <div className="text-gray-600">
              <p className="font-medium">
                Asset Id:{" "}
                {inspection.assetName || `Asset ${inspection.assetId}`}
              </p>
              {inspection.checklistName && (
                <p className="text-sm text-[#00A8A8] font-medium">
                  Checklist: {inspection.checklistName}
                </p>
              )}
              {inspection.assetLocation && (
                <p className="text-sm">{inspection.assetLocation}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Inspection ID: {inspection.id}
              </p>
            </div>
          </div>
        </div>

        {/* Inspection Details */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Inspection Details
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="flex items-center gap-3">
              <User size={18} className="text-gray-500" />
              <div>
                <p className="text-sm text-gray-500">Inspector</p>
                <p className="font-medium">{inspection.inspectorName}</p>
                <p className="text-xs text-gray-400">
                  {inspection.inspectorEmail}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Calendar size={18} className="text-gray-500" />
              <div>
                <p className="text-sm text-gray-500">Inspection Date</p>
                <p className="font-medium">
                  {formatDate(inspection.inspectionDate)}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <FileText size={18} className="text-gray-500" />
              <div>
                <p className="text-sm text-gray-500">Created</p>
                <p className="font-medium">
                  {formatDateTime(inspection.createdAt)}
                </p>
              </div>
            </div>

            {inspection.nextInspectionDate && (
              <div className="flex items-center gap-3">
                <Calendar size={18} className="text-gray-500" />
                <div>
                  <p className="text-sm text-gray-500">Next Inspection</p>
                  <p className="font-medium">
                    {formatDate(inspection.nextInspectionDate)}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Dynamic Asset-Specific Information */}
        <DynamicAssetFieldsViewer
          values={inspection as unknown as Record<string, any>}
          equipmentInventoryName={selectedAsset.equipmentInventoryName}
        />

        {/* Compliance Statistics */}
        {!inspection.isDraft && inspection.responses && inspection.responses.length > 0 && (() => {
          const total = inspection.responses.filter((r) => r.status && r.status !== "na").length;
          const complied = inspection.responses.filter((r) => r.status?.toLowerCase() === "pass").length;
          const partiallyComplied = inspection.responses.filter((r) => r.status?.toLowerCase() === "attention").length;
          const notComplied = inspection.responses.filter((r) => r.status?.toLowerCase() === "fail").length;
          const compliancePercentage = total > 0 ? Math.round((complied / total) * 100) : 0;

          return (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Question Compliance Summary
              </h2>
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-700">Overall Compliance</h3>
                  <span className={`text-sm font-medium px-3 py-1 rounded-full ${compliancePercentage >= 80
                      ? 'bg-green-100 text-green-800'
                      : compliancePercentage >= 50
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                    {compliancePercentage}%
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                      <FileText className="w-5 h-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Total Questions</p>
                      <p className="text-lg font-bold text-gray-800">{total}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Complied</p>
                      <p className="text-lg font-bold text-green-700">{complied}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Partially Complied</p>
                      <p className="text-lg font-bold text-yellow-700">{partiallyComplied}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                      <XCircle className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Not Complied</p>
                      <p className="text-lg font-bold text-red-700">{notComplied}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Inspection Results */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-gray-800">
              Inspection Results
            </h2>
            <div className="text-sm text-gray-600">
              {inspection.responses.length} items inspected
            </div>
          </div>

          {Object.entries(groupedResponses).map(([category, responses]) => (
            <div key={category} className="mb-6 last:mb-0">
              <h3 className="text-md font-medium text-gray-700 mb-4 pb-2 border-b-2 border-gray-300 flex items-center justify-between">
                <span>{category}</span>
                <span className="text-sm text-gray-500">
                  {responses.length} items
                </span>
              </h3>

              <div className="space-y-3">
                {responses.map((response, index) => (
                  <div
                    key={`${response.itemId}-${index}`}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow bg-white"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="mt-0.5">
                            {getStatusIcon(response.status)}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-800 mb-2">
                              {(response as any).question ||
                                getQuestionText(response.itemId)}
                            </h4>

                            <div className="flex items-center gap-3 flex-wrap">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">
                                  Response:
                                </span>
                                <span className="font-medium text-gray-900">
                                  {renderValue(response)}
                                </span>
                              </div>
                              <div
                                className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                                  response.status
                                )}`}
                              >
                                {response.status.charAt(0).toUpperCase() +
                                  response.status.slice(1)}
                              </div>
                            </div>
                          </div>
                        </div>

                        {response.comment && (
                          <div className="ml-8 mt-2 p-3 bg-gray-50 rounded-lg border-l-4 border-blue-400">
                            <div className="flex items-start gap-2">
                              <MessageSquare
                                size={14}
                                className="text-gray-500 mt-0.5"
                              />
                              <div>
                                <p className="text-xs text-gray-500 mb-1">
                                  Comment:
                                </p>
                                <p className="text-sm text-gray-700">
                                  {response.comment}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Photos */}
                    {response.photos && response.photos.length > 0 && (
                      <div className="mt-3">
                        <p className="text-sm text-gray-500 mb-2 flex items-center gap-1">
                          <Camera size={14} />
                          Photos ({response.photos.length})
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {response.photos.map((photo, photoIndex) => (
                            <button
                              key={photoIndex}
                              onClick={() =>
                                setSelectedPhoto(URL.createObjectURL(photo))
                              }
                              className="w-16 h-16 rounded border hover:shadow-md transition-shadow"
                            >
                              <img
                                src={URL.createObjectURL(photo)}
                                alt={`Photo ${photoIndex + 1}`}
                                className="w-full h-full object-cover rounded"
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        {/* Attachments */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          {inspection.attachments && inspection.attachments.length > 0 ? (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <FileText size={18} /> Attachments (
                {inspection.attachments.length})
              </h2>
              <div className="space-y-2">
                {inspection.attachments.map((url, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <FileText size={18} className="text-gray-500" />
                      <span className="text-sm text-gray-800 break-all">
                        {getFileNameFromUrl(url)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleViewFile(url)}
                        className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800"
                      >
                        Download
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <h1 className="text-lg font-semibold text-gray-800 mb-4">
              No attachments
            </h1>
          )}
        </div>
        {/* Overall Assessment */}

        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-6">
            Overall Assessment
          </h2>

          <div className="space-y-6">
            <div>
              <label className=" text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <MessageSquare size={16} />
                Overall Comments
              </label>
              <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
                <p className="text-gray-800 leading-relaxed">
                  {inspection.overallComments || "No comments provided"}
                </p>
              </div>
            </div>

            {inspection.correctiveActions && (
              <div>
                <label className=" text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <AlertTriangle size={16} className="text-orange-600" />
                  Corrective Actions Required
                </label>
                <div className="bg-orange-50 border-l-4 border-orange-400 p-4 rounded-lg">
                  <p className="text-orange-900 leading-relaxed">
                    {inspection.correctiveActions}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex flex-col sm:flex-row gap-3 justify-between">
            <div className="flex gap-3">
              {onDownloadReport && (
                <div className="relative">
                  <button
                    onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                    className="flex items-center gap-2 px-6 py-3 bg-[#00A8A5] text-white rounded-lg hover:bg-[#008A87] transition-colors font-medium"
                  >
                    <Download size={20} />
                    Download Report
                    <ChevronDown size={20} />
                  </button>
                  {showDownloadMenu && (
                    <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden z-10">
                      <button
                        onClick={() => {
                          setShowDownloadMenu(false);
                          onDownloadReport("pdf");
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3 text-gray-700 transition-colors border-b border-gray-100"
                      >
                        <FileText size={18} className="text-red-500" />
                        <span className="font-medium">Download PDF</span>
                      </button>
                      <button
                        onClick={() => {
                          setShowDownloadMenu(false);
                          onDownloadReport("excel");
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3 text-gray-700 transition-colors"
                      >
                        <FileSpreadsheet size={18} className="text-green-600" />
                        <span className="font-medium">Download Excel</span>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Photo Modal */}
        {selectedPhoto && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="relative max-w-4xl max-h-full">
              <button
                onClick={() => setSelectedPhoto(null)}
                className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
              >
                <XCircle size={32} />
              </button>
              <img
                src={selectedPhoto}
                alt="Inspection Photo"
                className="max-w-full max-h-full object-contain rounded-lg"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InspectionViewer;
